import { Form, Formik } from "formik";
import { useEffect, useState, useMemo } from "react";
import useStatistics from  "../../data/useStatistics";
import { Button, Typography } from "../../ui";
import InterestsModal from "./InterestsModal";
import {
  Card, CardDashedGraph
} from "../../widgets";
import { MetricsStatistics } from "../../widgets/MetricsStatistics";
import { StatisticsSort } from "../../widgets/Sort";
import "./Statistics.scss";
import { useLocation } from "react-router-dom";
import { Box, Modal } from "@mui/material";
import { ReactComponent as Close } from "../../assets/images/close.svg";
import useUser from "../../data/useUser";
import {
  INTERESTS_HIDDEN_USER_ID,
  applySpecialClientStatsOverrides,
  isSpecialStatsClient,
  MOCK_STATS_DATA,
  delayMockStatsLoading,
  getMockStatsMonth,
} from "./specialClientMockStats";

import { Geography, Devices, Ages, Genders, InterestsStatistics } from "../../widgets";

import {DOMAIN_API_HOST} from "../../configs/main";
import { 
  ResponsiveContainer, 
  BarChart, 
  CartesianGrid, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ReferenceLine, 
  Bar, 
  Cell 
} from 'recharts';

const AffinityBarChart = ({ counter_id, date_from, date_to, isMobile }) => {
  const [topData, setTopData] = useState(null);
  const [bottomData, setBottomData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `${DOMAIN_API_HOST}/api/v1/ya-metrika/segments?counter_id=${counter_id}&date_from=${new Date(date_from).toISOString().slice(0, 10)}&date_to=${new Date(date_to).toISOString().slice(0, 10)}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error, status: ${response.status}`);
        }

        const rawData = await response.json();
        const { top5, bottom5 } = processData(rawData);

        if (!top5 || !bottom5 || top5.length < 5 || bottom5.length < 5) {
          setError("Данные за этот период не найдены");
          setTopData(null);
          setBottomData(null);
        } else {
          setTopData(top5);
          setBottomData(bottom5);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Данные за этот период не найдены");
        setTopData(null);
        setBottomData(null);
      } finally {
        setLoading(false);
      }
    };

    if (counter_id && date_from && date_to) {
      fetchData();
    } else {
      setTopData(null);
      setBottomData(null);
    }
  }, [counter_id, date_from, date_to]);

  const processData = (rawData) => {
    if (!rawData || Object.keys(rawData).length === 0) {
      return { top5: [], bottom5: [] };
    }

    const byIabName = {};
    Object.entries(rawData).forEach(([name, values]) => {
      const displayName = values.iab_name || name;
      const current = byIabName[displayName];
      if (!current || values.affinity > current.affinity) {
        byIabName[displayName] = {
          name: displayName,
          users: values.users,
          affinity: values.affinity,
          originalAffinity: values.affinity,
        };
      }
    });

    const sorted = Object.values(byIabName)
      .sort((a, b) => b.affinity - a.affinity);

    const top5 = sorted.slice(0, 5);          
    const bottom5Raw = sorted.slice(-5).reverse();

    const maxTopValue = Math.max(...top5.map(item => item.affinity));
    const minBottomValue = Math.min(...bottom5Raw.map(item => item.affinity));

    const scalingFactor = maxTopValue / Math.abs(minBottomValue);
    const bottom5 = bottom5Raw.map(item => ({
      ...item,
      affinity: -Math.abs(item.affinity) * scalingFactor * 0.8
    }));

    return { top5, bottom5 };
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ 
          backgroundColor: 'rgba(0, 0, 0, 0.8)', 
          padding: '10px', 
          borderRadius: '5px', 
          color: 'white',
          fontSize: '14px'
        }}>
          <p><strong>{payload[0].payload.name}</strong></p>
          <p>Пользователи: {payload[0].payload.users}</p>
          <p>Афинити индекс: {Math.abs(payload[0].payload.originalAffinity ?? payload[0].payload.affinity)}%</p>
        </div>
      );
    }
    return null;
  };

  if (!counter_id) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '500px',
        color: '#666',
        fontSize: '16px'
      }}>
        Выберите счётчик
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '500px',
        color: '#666',
        fontSize: '16px'
      }}>
        Загрузка данных...
      </div>
    );
  }

  if (error || !topData || !bottomData) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '500px',
        color: '#666',
        fontSize: '18px'
      }}>
        {error || "Нет данных для отображения"}
      </div>
    );
  }

  const maxTop = Math.max(...topData.map(item => item.affinity));
  const paddingTop = maxTop * 0.1;
  const topDomainMin = -(maxTop + paddingTop);
  const topDomainMax = maxTop + paddingTop;

  const bottomAbs = Math.max(...bottomData.map(item => Math.abs(item.affinity)));
  const paddingBottom = bottomAbs * 0.1;
  const bottomDomainMin = -(bottomAbs + paddingBottom);
  const bottomDomainMax = bottomAbs + paddingBottom;

  return (
    <div style={{ width: '500px', maxWidth: '1200px', margin: '0 auto', overflowX: isMobile ? "auto" : "none" }}>
      <p style={{ textAlign: "end", paddingRight: "20px", fontSize: isMobile ? 12 : 16 }}>Топ 5 с начала</p>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart 
          layout="vertical" 
          data={topData} 
          margin={{ top: 20, right: 10, left: 10, bottom: -20 }}
          barCategoryGap={10}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            type="number"
            domain={[topDomainMin, topDomainMax]}
            tick={false}
            axisLine={false}
          />
          <YAxis 
            dataKey="name" 
            type="category" 
            width={isMobile ? 100 : 200} 
            tick={{ fontSize: isMobile ? 12 : 14 }}
            padding={{ top: 10, bottom: 10 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine x={0} stroke="#999" />
          <Bar 
            dataKey="affinity" 
            name="Афинити индекс"
            radius={[0, 4, 4, 0]}
          >
            {topData.map((entry, index) => (
              <Cell 
                key={`top-cell-${index}`} 
                fill={entry.affinity >= 0 ? "#3f73f9" : "#ff3232"} 
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <ResponsiveContainer width="100%" height={250}>
        <BarChart 
          layout="vertical" 
          data={bottomData} 
          margin={{ top: 0, right: 10, left: 10, bottom: -10 }}
          barCategoryGap={10}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            type="number"
            domain={[bottomDomainMin, bottomDomainMax]}
            tick={false}
            axisLine={false}
          />
          <YAxis 
            dataKey="name" 
            type="category" 
            width={isMobile ? 100 : 200} 
            tick={{ fontSize: isMobile ? 12 : 14 }}
            padding={{ top: 10, bottom: 10 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine x={0} stroke="#999" />
          <Bar 
            dataKey="affinity" 
            name="Афинити индекс"
            radius={[0, 4, 4, 0]}
          >
            {bottomData.map((entry, index) => (
              <Cell 
                key={`bottom-cell-${index}`} 
                fill={entry.affinity >= 0 ? "#3f73f9" : "#ff3232"} 
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <p style={{ textAlign: "center", fontSize: isMobile ? 12 : 16, paddingRight: "80px" }}>Топ 5 с конца</p>
    </div>
  );
};


const FORMIK_INITIAL_VALUES = {
  selected_campaigns: [],
  selected_goals: [],
  metric_counter: null,
  selected_ads: [],
  graph_type: "campaigns",
  media_plan: {
    date_start: null,
    date_end: null,
  },
}

const FORMIK_INITIAL_VALUES_DATA = (camp, metric) => {
  return {
    selected_campaigns: [camp],
    selected_goals: [],
    metric_counter: metric,
    selected_ads: [],
    graph_type: "campaigns",
    media_plan: {
      date_start: null,
      date_end: null,
    },
  }
}

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};






const SPECIAL_USER_ID = "0aeb7a8a-7120-49ab-b62c-13158dc69304";
const BREAKDOWN_WITHOUT_CAMPAIGN_USER_ID = "0aeb7a8a-7120-49ab-b62c-13158dc69304";
const SPECIAL_USER_HIDDEN_METRICS = ["cr", "cpa"];
const WIDE_INTERESTS_USER_ID = "84149b79-02d9-4919-8a92-e2531594dc4b";
function Statistics() {
  const location = useLocation();
  const [err, setErr] = useState(false)
  const [modal, setModal] = useState(false)
  const [interestsModal, setInterestsModal] = useState(false)
  const userData = useUser()
  let campaign_id = location?.state?.campaign_id;
  let campaign_ids = location?.state?.campaign_ids;
  let counter_id = location?.state?.counter_id;
  const [formikValues, setFormikValues] = useState(FORMIK_INITIAL_VALUES);
  const [hiddenMetricKeys, setHiddenMetricKeys] = useState([]);
  const [activeDateStart, setActiveDateStart] = useState(null);
  const [activeDateEnd, setActiveDateEnd] = useState(null);
  let type = "day" 
  // if ((formikValues.media_plan.date_end-formikValues.media_plan.date_start)/(1000 * 60 * 60 * 24) < 1) type = "hour"
  // if ((formikValues.media_plan.date_end-formikValues.media_plan.date_start)/(1000 * 60 * 60 * 24) > 31) type="week"

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const updateHiddenMetrics = async () => {
      try {
        const selected = formikValues.selected_campaigns?.filter((id) => id !== "all_campaigns") || [];
        if (selected.length < 1) {
          setHiddenMetricKeys([]);
          return;
        }
        const headers = {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        };
        const responses = await Promise.all(
          selected.map((id) =>
            fetch(`${DOMAIN_API_HOST}/api/v1/campaigns/${id}`, { method: "GET", headers })
          )
        );
        const campaigns = await Promise.all(
          responses.map(async (res) => (res.ok ? res.json() : null))
        );
        const targets = campaigns
          .map((c) => c?.optimization?.optimization_target)
          .filter(Boolean);
        if (targets.length !== selected.length) {
          // если не удалось получить все цели — ничего не скрываем
          setHiddenMetricKeys([]);
          return;
        }
        const allClicks = targets.every((t) => t === "CLICKS");
        const allShows = targets.every((t) => t === "SHOWS");
        if (allClicks) {
          setHiddenMetricKeys(["ecpm"]);
        } else if (allShows) {
          setHiddenMetricKeys(["cpc"]);
        } else {
          setHiddenMetricKeys([]);
        }
      } catch (e) {
        setHiddenMetricKeys([]);
      }
    };
    updateHiddenMetrics();
  }, [formikValues.selected_campaigns]);

  const effectiveHiddenMetricKeys = useMemo(() => {
    if (userData.user?.id !== SPECIAL_USER_ID) {
      return hiddenMetricKeys;
    }
    return [...new Set([...hiddenMetricKeys, ...SPECIAL_USER_HIDDEN_METRICS])];
  }, [hiddenMetricKeys, userData.user?.id]);

  const adjustDateEnd = (dateEnd, userId) => {
    if (!dateEnd) return dateEnd;
    
    const SPECIAL_CLIENT_ID = "a1856853-7f15-427a-ad58-3c86aca153de";
    
    if (userId === SPECIAL_CLIENT_ID) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const endDate = new Date(dateEnd);
      endDate.setHours(0, 0, 0, 0);
      
      if (endDate.getTime() === today.getTime()) {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return yesterday;
      }
    }
    
    return dateEnd;
  };

  // Мемоизируем скорректированные даты (DatePicker уже корректирует дату в formik, но для безопасности оставляем корректировку здесь)
  const adjustedDateStart = useMemo(() => formikValues.media_plan.date_start, [formikValues.media_plan.date_start]);
  const adjustedDateEnd = useMemo(() => adjustDateEnd(formikValues.media_plan.date_end, userData.user?.id), [formikValues.media_plan.date_end, userData.user?.id]);

  const adjustedActiveDateStart = useMemo(() => activeDateStart, [activeDateStart]);
  const adjustedActiveDateEnd = useMemo(() => adjustDateEnd(activeDateEnd, userData.user?.id), [activeDateEnd, userData.user?.id]);

  // ── единый запрос для devices/ages/genders ──
  const [breakdownData, setBreakdownData] = useState(null);

  useEffect(() => {
    let isCancelled = false;

    const fetchBreakdown = async () => {
      const counterId = formikValues.metric_counter;
      const dateFrom = adjustedActiveDateStart;
      const dateTo = adjustedActiveDateEnd;
      const userId = userData.user?.id;
      const campaigns =
        userId === BREAKDOWN_WITHOUT_CAMPAIGN_USER_ID
          ? []
          : (formikValues.selected_campaigns || []).filter(
              (id) => id && id !== "all_campaigns" && id !== "0"
            );

      if (!counterId || !dateFrom) {
        setBreakdownData(null);
        return;
      }

      // mock-данные для special клиента
      if (isSpecialStatsClient(userId)) {
        await delayMockStatsLoading();
        if (isCancelled) return;
        const monthKey = getMockStatsMonth(dateFrom, dateTo);
        const mock = monthKey ? MOCK_STATS_DATA[monthKey] : null;
        setBreakdownData(mock ? { devices: mock.devices, ages: mock.ages, genders: mock.genders } : { devices: [], ages: [], genders: [] });
        return;
      }

      try {
        let url = `${DOMAIN_API_HOST}/api/v1/ya-metrika/stat/breakdown?counter_id=${counterId}&date_from=${dateFrom?.toLocaleDateString("en-CA")}&date_to=${dateTo?.toLocaleDateString("en-CA")}`;
        campaigns.forEach((id) => { url += `&campaign_ids=${id}`; });

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });

        if (!response.ok) {
          // fallback — без campaign_ids
          if (campaigns.length > 0) {
            const fallbackUrl = `${DOMAIN_API_HOST}/api/v1/ya-metrika/stat/breakdown?counter_id=${counterId}&date_from=${dateFrom?.toLocaleDateString("en-CA")}&date_to=${dateTo?.toLocaleDateString("en-CA")}`;
            const fbResponse = await fetch(fallbackUrl, {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            if (fbResponse.ok) {
              const fbData = await fbResponse.json();
              if (!isCancelled) setBreakdownData(fbData);
            } else {
              if (!isCancelled) setBreakdownData({ devices: [], ages: [], genders: [] });
            }
          } else {
            if (!isCancelled) setBreakdownData({ devices: [], ages: [], genders: [] });
          }
          return;
        }

        const data = await response.json();
        const hasAny = data.devices?.some(i => i.value > 0) || data.ages?.some(i => i.value > 0) || data.genders?.some(i => i.value > 0);

        if (!hasAny && campaigns.length > 0) {
          const fallbackUrl = `${DOMAIN_API_HOST}/api/v1/ya-metrika/stat/breakdown?counter_id=${counterId}&date_from=${dateFrom?.toLocaleDateString("en-CA")}&date_to=${dateTo?.toLocaleDateString("en-CA")}`;
          const fbResponse = await fetch(fallbackUrl, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          });
          if (fbResponse.ok) {
            const fbData = await fbResponse.json();
            if (!isCancelled) setBreakdownData(fbData);
          } else {
            if (!isCancelled) setBreakdownData(data);
          }
        } else {
          if (!isCancelled) setBreakdownData(data);
        }
      } catch (error) {
        console.error("Error fetching breakdown:", error);
        if (!isCancelled) setBreakdownData(null);
      }
    };

    if (formikValues.metric_counter && adjustedActiveDateStart) {
      fetchBreakdown();
    } else {
      setBreakdownData(null);
    }

    return () => { isCancelled = true; };
  }, [formikValues.metric_counter, adjustedActiveDateStart, adjustedActiveDateEnd, formikValues.selected_campaigns, userData.user?.id]);

  useEffect(() => {
    if (adjustedDateStart && adjustedDateEnd && activeDateStart === null && activeDateEnd === null) {
      setActiveDateStart(adjustedDateStart);
      setActiveDateEnd(adjustedDateEnd);
    }
  }, [adjustedDateStart, adjustedDateEnd]);

  let {data, error, isLoading} = useStatistics(formikValues.metric_counter,adjustedActiveDateStart,
      adjustedActiveDateEnd,formikValues.selected_campaigns,formikValues.selected_goals,type,formikValues.selected_ads,formikValues.graph_type, userData.user?.id);

  const statisticsData = useMemo(() => {
    if (!data?.metrics) {
      return {
        metrics: ["shows", "reach", "clicks", "leads", "spent", "ctr", "cpc", "ecpm", "cr", "cpa"],
        time_intervals: [],
        data: [],
        totals: [0, 0, 0, 0, 0, 0, 0, 0, null, null],
      };
    }

    return applySpecialClientStatsOverrides(
      data,
      userData.user?.id,
      adjustedActiveDateStart,
      adjustedActiveDateEnd,
      formikValues.selected_campaigns
    );
  }, [data, userData.user?.id, adjustedActiveDateStart, adjustedActiveDateEnd, formikValues.selected_campaigns]);

  useEffect(()=>{
    if(error && String(error).includes('Error: {"detail":"date_from не может быть раньше текущей даты более чем на 12 месяцев"}')){
      setErr(true)
    }else{
      setErr(false)
    }
  },[error])
  useEffect(()=>{
    if (err) {
      setModal(true)
  }
  },[err])
  // console.log("updated", formikValues)
  return (
    <>
    <Formik
      initialValues={
        Array.isArray(campaign_ids) && campaign_ids.length > 0
          ? {
              ...FORMIK_INITIAL_VALUES,
              selected_campaigns: campaign_ids,
              metric_counter: counter_id,
            }
          : campaign_id
          ? FORMIK_INITIAL_VALUES_DATA(campaign_id, counter_id)
          : FORMIK_INITIAL_VALUES
      }
      onSubmit={(value) => {
        console.log('export') || console.log(value)
      }}
      innerRef={formikAction => {
        if (!formikAction) {
          return;
        }
        const { values } = formikAction;
        if (
          values.selected_campaigns.length === formikValues.selected_campaigns.length
          && values.selected_campaigns.every(
            (value, index) => value === formikValues.selected_campaigns[index]
          )
          && values.media_plan.date_start === formikValues.media_plan.date_start
          && values.media_plan.date_end === formikValues.media_plan.date_end
          && values.metric_counter === formikValues.metric_counter
          && values.graph_type === formikValues.graph_type
          && values.selected_goals.length === formikValues.selected_goals.length
          && values.selected_ads.length === formikValues.selected_ads.length
          && values.selected_goals.every(
            (value, index) => value === formikValues.selected_goals[index]
          )
        ) {
          return;
        }
        setFormikValues(formikAction.values);
      }}
      
    >
      <Form>
        <div className="Statistics">
          <Typography variant="h1" color="dark-gray">
            Статистика
          </Typography>
          <StatisticsSort 
            userData={userData} 
            onRefresh={() => {
              setActiveDateStart(adjustedDateStart);
              setActiveDateEnd(adjustedDateEnd);
            }}
          />
          <div style={{ marginTop: "10px", marginBottom: "10px" }} className="line"></div>
          <div style={{ marginBottom: "20px"}} className="container">
          <MetricsStatistics data={statisticsData?.totals} isMobile={isMobile} hiddenMetricKeys={effectiveHiddenMetricKeys} isLoading={isLoading} />
            {/* <InterestsStatistics data={InterestData} />
            <Devices data={fiterdata2} />
            <Geography data={fiterdata2} /> */} 
          </div>
          <div className="widgets-container">
            <div>
              <Genders
                counter_id={formikValues.metric_counter}
                date_from={adjustedActiveDateStart}
                date_to={adjustedActiveDateEnd}
                selected_campaigns={formikValues.selected_campaigns}
                user_id={userData.user?.id}
                data={breakdownData ? breakdownData.genders : null}
              />
            </div>

            <div>
              <Ages
                counter_id={formikValues.metric_counter}
                date_from={adjustedActiveDateStart}
                date_to={adjustedActiveDateEnd}
                selected_campaigns={formikValues.selected_campaigns}
                user_id={userData.user?.id}
                data={breakdownData ? breakdownData.ages : null}
              />
            </div>

            <div>
              <Devices
                counter_id={formikValues.metric_counter}
                date_from={adjustedActiveDateStart}
                date_to={adjustedActiveDateEnd}
                selected_campaigns={formikValues.selected_campaigns}
                user_id={userData.user?.id}
                data={breakdownData ? breakdownData.devices : null}
              />
            </div>

            <div>
              <Geography
                counter_id={formikValues.metric_counter}
                date_from={adjustedActiveDateStart}
                date_to={adjustedActiveDateEnd}
                selected_campaigns={formikValues.selected_campaigns}
              />
            </div>
          </div>
          {userData.user.id !== INTERESTS_HIDDEN_USER_ID && (
            <div className="interests-container" style={{ justifyContent: userData.user.id === WIDE_INTERESTS_USER_ID ? "center" : "flex-start" }}>
              <div className={userData.user.id === WIDE_INTERESTS_USER_ID ? "interests-chart wide" : "interests-chart"}>
                <Typography variant="h2" color="dark-gray">
                  Интересы
                </Typography>
                <AffinityBarChart counter_id={formikValues.metric_counter} date_from={adjustedActiveDateStart} date_to={adjustedActiveDateEnd} isMobile={isMobile} />
              </div>
              {userData.user.id !== WIDE_INTERESTS_USER_ID && (
                <div className="interests-stats">
                  <InterestsStatistics counter_id={formikValues.metric_counter} date_from={adjustedActiveDateStart} date_to={adjustedActiveDateEnd} isMobile={isMobile} />
                </div>
              )}
            </div>
          )}
          <Card>
            <CardDashedGraph isMobile={isMobile} title type={type} chartData={statisticsData} budget={statisticsData.totals} userData={userData} hiddenMetricKeys={effectiveHiddenMetricKeys} />
          </Card>
          {userData.user.id === INTERESTS_HIDDEN_USER_ID && (
            <div className="interests-open-button">
              <Button variant="primary" style={{width: "300px", borderRadius: "10px"}} onClick={() => setInterestsModal(true)}>
                Отчёты по интересам
              </Button>
            </div>
          )}


          {/* <div style={{ marginTop: "50px"s, display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", width: "100%"}}>
            <svg style={{opacity: ".7", width: "120px", height: "120px"}} xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="#42454c" viewBox="0 0 256 256"><path d="M160,42A86.11,86.11,0,0,0,82.43,90.88,62,62,0,1,0,72,214h88a86,86,0,0,0,0-172Zm0,160H72a50,50,0,0,1,0-100,50.67,50.67,0,0,1,5.91.35A85.61,85.61,0,0,0,74,128a6,6,0,0,0,12,0,74,74,0,1,1,74,74Zm-6-74V88a6,6,0,0,1,12,0v40a6,6,0,0,1-12,0Zm16,36a10,10,0,1,1-10-10A10,10,0,0,1,170,164Z"></path></svg>
            <p style={{fontSize: "20px", textAlign: "center", width: "100%", opacity: ".7"}}>Статистика временно недоступна.</p>
          </div> */}


        </div>
      </Form>
    </Formik>
    <Modal open={modal} onClose={() => setModal(false)}>
      <Box sx={style}>
        <h4 style={{width:"100%", textAlign:'center'}}>Cтатистика за более чем 12 месяца не доступна</h4>
      <Close onClick={() => setModal(false)} style={{position:'absolute', top:10,right:10, cursor:'pointer'}} />
      </Box>
    </Modal>
    <InterestsModal open={interestsModal} onClose={() => setInterestsModal(false)} />
    </>
  );
}

export default Statistics;